if (!customElements.get('product-selector')) {
  class ProductSelector extends HTMLElement {
    constructor() {
      super();
      this.inputWrapper = '[data-input-wrapper]';
      this.variants = JSON.parse(
        this.querySelector('[type="application/json"]').textContent
      );
      this.form = document.querySelector(
        `#ProductForm-${this.dataset.sectionId}`
      );
      this.purchaseOptions = document.querySelector(
        `#Product-Purchase-Options-${this.dataset.sectionId}`
      );
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );
      this.installmentsForm = document.querySelector(
        `#Product-Installments-${this.dataset.installmentsFormId}` // check: `${this.dataset.installmentsFormId}`
      );
      this.userHasSelectedVariant = false; // Track if user has made a selection
      this.addEventListener('change', this.onVariantChange);

      // The sticky bar sits outside this element, so its selects do not bubble
      // a change event to us. Proxy them onto the matching radio in the main
      // picker instead of duplicating any variant logic: checking that radio
      // and firing its change event puts us back on the normal path, and keeps
      // the main picker the single source of truth for what is selected.
      //
      // Bound to the bar rather than to the selects themselves, because
      // renderProductInfo() replaces .product__sticky-cart-wrapper wholesale on
      // every variant change - listeners on the selects would be thrown away
      // with the old markup, and the bar would go dead after one use.
      this.productBar?.addEventListener('change', event => {
        const select = event.target.closest('select[data-sticky-option]');
        if (!select) return;
        const radio = Array.from(
          this.querySelectorAll('input[type="radio"]')
        ).find(
          input => input.name === select.name && input.value === select.value
        );
        if (!radio || radio.disabled) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.addEventListener('keydown', this.onKeyDown);
      
      // Uncheck all variants initially if product has multiple variants
      if (this.variants.length > 1) {
        this.uncheckAllVariants();
        // Disable button and show "Select Size" for multi-variant products
        this.form?.toggleAddButton(true, window.variantStrings.selectSize);
        // The sizes have just been unchecked, so any stock notice rendered for
        // a ?variant= in the URL now describes a size the customer cannot see
        // selected. Hide it until they choose one.
        this.hideInventoryNotice();
      }
      
      this.updateOptions();
      this.updateVariantStatuses();
      this.updateVariant();
      this.updateMedia();
      this.scrollToImage();

      // Set initial button state for single variant products
      if (this.variants.length === 1) {
        this.updateButtonText();
      }
    }

    updateVariantStatuses() {
      const checkedOption = this.querySelector(
        this.inputWrapper
      ).querySelector(':checked');
      
      // If no option is checked (initial state), don't update variant statuses
      if (!checkedOption) {
        return;
      }
      
      const optionOneValue = checkedOption.value;
      const selectedOptionOneVariants = this.variants.filter(
        variant => optionOneValue === variant.option1
      );
      const inputWrappers = [
        ...this.querySelectorAll(this.inputWrapper)
      ];

      inputWrappers.forEach((option, index) => {
        if (index === 0) return;

        const previousOptionSelected =
          inputWrappers[index - 1].querySelector(':checked').value;
        const listOfAvailableOptions = selectedOptionOneVariants
          .filter(
            variant =>
              variant.available &&
              variant[`option${index}`] === previousOptionSelected
          )
          .map(variantOption => variantOption[`option${index + 1}`]);

        const optionInputs = [
          ...option.querySelectorAll('input[type="radio"], option')
        ];

        optionInputs.forEach(input => {
          const inputIsDisabled = !listOfAvailableOptions.includes(
            input.getAttribute('value')
          );

          input.disabled = inputIsDisabled;
          if (input.tagName.toLowerCase() === 'option') {
            const escapedValue = input.value.replace(/"/g, '\\"');
            const selector = `button[data-value="${escapedValue}"]`;
            const button = input
              .closest('dropdown-input')
              .querySelector(selector);
            if (button) {
              button.disabled = inputIsDisabled;
            }
          }
          if (!this.productBar) return;
          const inputId = input.dataset.id || input.id;
          const productBarInput = this.productBar.querySelector(
            `option[data-id="${inputId}"]`
          );
          if (!productBarInput) return;
          productBarInput.disabled = inputIsDisabled;
        });
      });

      // add .is-active class to selected swatch with coming from URL
      this.querySelectorAll('input[type="radio"]').forEach(input => {
        if (input.checked) {
          input.nextElementSibling.classList.add('is-active');
        }
      });


      this.updateColorSwatchLabel();
    }

    onKeyDown(event) {
      if (event.key === 'Enter') {
        const target = event.target;
        if (target.classList.contains('swatch')) {
          target.click();
        }
      }
    }

    onVariantChange(event) {
      if (event.target.type === 'number') return;
      
      // Mark that user has made a selection
      this.userHasSelectedVariant = true;
      
      this.updateOptions();
      if (this.currentVariant === this.getVariantData()) return;

      this.updateVariant();
      this.updateVariantStatuses();
      this.productBarUpdateOptions();
      this.updatePickupAvailability();
      this.form.removeAttribute('data-has-selling-plan');
      this.form.handleErrorMessage();

      if (!this.currentVariant) {
        this.form.toggleAddButton(true, '');
        this.setUnavailable();
        return;
      }

      // Update button text based on current state
      this.updateButtonText();

      if (!this.closest('.product').classList.contains('highlighted-product')) this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateSKU();
      this.updateColorSwatchLabel();
      this.updateMedia();
      this.scrollToImage();
    }

    updateOptions() {
      this.querySelectorAll('input[type="radio"]').forEach(input => {
        if (input.disabled) {
          input.nextElementSibling.classList.toggle(
            "is-active",
            false
          );
        } else {
          input.nextElementSibling.classList.toggle(
            "is-active",
            input.checked
          );
        }
      });
      this.options = Array.from(
        this.querySelectorAll('input[type="radio"]:checked, select'),
        el => ({ name: el.dataset.name || el.name, value: el.value })
      );
    }

    productBarUpdateOptions() {
      if (this.productBar) {
        Array.from(this.productBar.querySelectorAll('select')).map(
          selector => {
            selector.value = this.options.find(
              option => option.name === selector.name
            ).value;
          }
        );
        // update dropdowns in product bar
        this.productBar.querySelectorAll('dropdown-input').forEach(
          dropdown => {
            // update dropdown list with usage update() of 'class DropdownInput extends HTMLElement' in global.js
            dropdown.update();
          }
        );
      }
    }

    updateInventoryNotice(html) {
      const destination = this.closest('.product').querySelector(
        '[data-inventory-notice]'
      );
      if (!destination) return;

      // Both directions matter. The old version only copied when the incoming
      // markup had a notice, so moving from a low-stock size to a well-stocked
      // one left the previous size's warning on screen.
      const source = html.querySelector('[data-inventory-notice]');
      destination.innerHTML = source ? source.innerHTML : '';
      destination.hidden = destination.innerHTML.trim() === '';
    }

    hideInventoryNotice() {
      const notice = this.closest('.product')?.querySelector(
        '[data-inventory-notice]'
      );
      if (notice) notice.hidden = true;
    }

    updatePickupAvailability() {
      const pickUpAvailabilityNode = document.querySelector(
        'pickup-availability'
      );

      if (!pickUpAvailabilityNode || !this.currentVariant) return;

      pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailabilityNode.setAttribute('available', '');
        pickUpAvailabilityNode.fetchAvailability(
          pickUpAvailabilityNode.dataset.variantId
        );

        return;
      }

      pickUpAvailabilityNode.removeAttribute('available');
      pickUpAvailabilityNode.innerHTML = '';
    }

    updateVariant() {
      this.currentVariant = this.getVariantData();
    }

    getVariantData() {
      if (this.options.length === 0) {
        return null; // No options selected
      }
      
      return this.variants.find(variant => {
        return !variant.options
          .map(
            (option, index) => this.options[index].value === option
          )
          .includes(false);
      }) || null;
    }

    updateMedia() {
      // console.log(this.currentVariant);
      if (!this.currentVariant || !this.currentVariant.featured_media)
        return;

      const productMedia = this.closest('.product').querySelector('product-media');
      productMedia.setActiveMedia(
        this.currentVariant.featured_media.id
      );
    }

    updateSKU() {
      if (document.querySelector(".product__sku"))
        document.querySelector(".product__sku span").innerText =
          this.currentVariant.sku || "";
    }

    updateURL(sellingPlanId) {
      if (!this.currentVariant) return;
      const params = new URLSearchParams(window.location.search);
      params[params.has('variant') ? 'set' : 'append'](
        'variant',
        this.currentVariant.id
      );
      if (sellingPlanId) {
        params[params.has('selling_plan') ? 'set' : 'append'](
          'selling_plan',
          sellingPlanId
        );
      } else {
        params.delete('selling_plan');
      }
      window.history.replaceState(
        {},
        '',
        `${this.dataset.url}?${params.toString()}`
      );
    }

    updateVariantInput() {
      const inputs = [
        this.querySelector('[name="id"'),
        this.installmentsForm?.querySelector('[name="id"]')
      ];
      inputs.forEach(input => {
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    setUnavailable() {
      const submitButton = this.form.submitButton;
      const price = this.querySelector(
        `#Product-Price-${this.dataset.sectionId}`
      );

      if (!submitButton) return;
      submitButton.textContent = window.variantStrings.unavailable;
      if (price) price.classList.add('visibility-hidden');
    }

    renderProductInfo() {
      const params = new URLSearchParams(window.location.search);
      params[params.has('variant') ? 'set' : 'append'](
        'variant',
        this.currentVariant.id
      );
      if (!this.closest('.product').classList.contains('highlighted-product')) {
        params.append('section_id', this.dataset.sectionId);
      }

      fetch(`${this.dataset.url}?${params.toString()}`)
        .then(response => response.text())
        .then(responseText => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );
          [
            `#Product-Price-${this.dataset.sectionId}`,
            `#Product-Purchase-Options-${this.dataset.sectionId}`,
            '.product__sticky-cart-wrapper',
          ].map(liveRegionSelector => {
            const destination = document.querySelector(
              liveRegionSelector
            );
            const source = html.querySelector(liveRegionSelector);

            if (destination && source) {
              destination.classList.remove('visibility-hidden');
              destination.innerHTML = source.innerHTML;
            }
          });

          this.updateInventoryNotice(html);
        });
    }

    updateColorSwatchLabel() {
      const activeCircleSwatch = this.querySelector(".swatch.swatch--labeled.is-active");
      if (!activeCircleSwatch) return;
      const activeCircleSwatchLabel = activeCircleSwatch.querySelector('span').innerText;
      const legendToDisplayLabel = activeCircleSwatch.closest(".product__swatches").previousElementSibling;

      if (!legendToDisplayLabel.querySelector('span')) {
        legendToDisplayLabel.innerHTML += `<span>${activeCircleSwatchLabel}</span>`;
      } else {
        legendToDisplayLabel.querySelector("span").innerText = activeCircleSwatchLabel;
      }
    }

    uncheckAllVariants() {
      // Uncheck all variant radio buttons to prevent auto-selection
      this.querySelectorAll('input[type="radio"]').forEach(input => {
        input.checked = false;
        // Remove is-active class from swatches
        if (input.nextElementSibling) {
          input.nextElementSibling.classList.remove('is-active');
        }
      });
    }

    updateButtonText() {
      // Update button text based on user selection status
      if (this.variants.length > 1 && !this.userHasSelectedVariant) {
        // Disabled button with "Select Size" text
        this.form?.toggleAddButton(true, window.variantStrings.selectSize);
        this.showButton();
      } else if (this.currentVariant && this.currentVariant.available) {
        // Enabled button with "Add to Cart" text
        this.form?.toggleAddButton(false, '');
        this.showButton();
      } else if (this.currentVariant && !this.currentVariant.available) {
        // Hide button when variant is unavailable
        this.hideButton();
      } else {
        // Hide button for unavailable
        this.hideButton();
      }
    }

    showButton() {
      // Show the add to cart button
      const button = this.form?.querySelector('.product-selector__submit');
      if (button) {
        button.style.display = '';
        button.classList.remove('hidden');
      }
    }

    hideButton() {
      // Hide the add to cart button
      const button = this.form?.querySelector('.product-selector__submit');
      if (button) {
        button.style.display = 'none';
        button.classList.add('hidden');
      }
    }

    scrollToImage() {
      this
        .closest(".product")
        .querySelectorAll("[data-product-images]")
        .forEach(img => {
          if (
            this.currentVariant && 
            this.currentVariant.id &&
            img
              .getAttribute("data-product-images")
              .includes(this.currentVariant.id)
          )
            img.scrollIntoView({
              behavior: "smooth"
            });
        });
    }
  }
  customElements.define('product-selector', ProductSelector);
}